import { useSyncExternalStore } from 'react';
import { Paperclip, X } from '../../icons';
import { formatBytes } from '../../lib/attachments';
import { tShared, getSharedComponentLang, subscribeSharedComponentLang } from '../../lib/shared-i18n';
import type { UseAttachmentUploadReturn } from './useAttachmentUpload';
import './AttachmentUploader.css';

/**
 * AttachmentUploader (cross-domain) — chat MessageInput 의 attachment chip-list + Paperclip
 * picker button + hidden file input 을 도메인 중립 컴포넌트로 추출. mail ComposeModal / 향후
 * board, calendar 등에서 동일 컴포넌트 재사용 (pattern-shared-component-extraction-cross-domain-7-atomic
 * 의 *2번째 정착 사례*).
 *
 * 사용 패턴:
 *   const uploadState = useAttachmentUpload();
 *   <AttachmentUploader state={uploadState} disabled={busy} />
 *
 * 외부 wrapper 가 정렬·여백 컨트롤. 본 컴포넌트는 *기능 묶음* 만 (chip-list + button + error).
 */

export interface AttachmentUploaderProps {
  state: UseAttachmentUploadReturn;
  /** 외부 사정 (전송 중 등) 으로 picker 비활성. uploadingFile 동안은 자동 disabled. */
  disabled?: boolean;
  /**
   * 버튼에 아이콘과 함께 보일 텍스트(옵션, 2026-10-14 · 옛 가지 e19b9007f 이식). 미지정이면 종전과 같다
   * (아이콘만) — 채팅·메일 등 다른 화면은 이 prop 을 안 쓴다. 캘린더 일정 창의 「첨부파일」 라벨용.
   */
  label?: string;
}

export function AttachmentUploader({ state, disabled = false, label }: AttachmentUploaderProps) {
  useSyncExternalStore(subscribeSharedComponentLang, getSharedComponentLang, getSharedComponentLang);
  const {
    attachments,
    uploadingFile,
    uploadPercent,
    error,
    fileInputRef,
    handleAttachClick,
    handleFileSelected,
    handleRemoveAttachment,
  } = state;

  // Loop1 F3 — yaml SSOT (maxItems: 10) 와 정합한 UI 차단.
  /* 2026-09-10 — 상한은 화면마다 다를 수 있다(AI 컴포저는 5). 훅이 알려 주는 값을 쓴다. */
  const atLimit = attachments.length >= state.maxItems;
  const buttonDisabled = disabled || !!uploadingFile || atLimit;
  const showChips = attachments.length > 0 || uploadingFile != null;

  return (
    <div className="attachment-uploader">
      {showChips && (
        <ul className="attachment-uploader-chips" role="list" aria-label={tShared('shared.upload.list_aria')}>
          {attachments.map((a) => (
            <li key={a.fileId} className="attachment-uploader-chip" role="listitem">
              <Paperclip size={12} aria-hidden />
              <span className="attachment-uploader-chip-name" title={a.name}>
                {a.name}
              </span>
              <span className="attachment-uploader-chip-size">{formatBytes(a.sizeBytes)}</span>
              <button
                type="button"
                className="attachment-uploader-chip-remove"
                onClick={() => handleRemoveAttachment(a.fileId)}
                aria-label={tShared('shared.upload.remove_aria', { name: a.name })}
              >
                <X size={12} />
              </button>
            </li>
          ))}
          {uploadingFile && (
            <li
              className="attachment-uploader-chip attachment-uploader-chip--uploading"
              role="listitem"
              aria-live="polite"
            >
              <Paperclip size={12} aria-hidden />
              <span className="attachment-uploader-chip-name">{uploadingFile}</span>
              <span className="attachment-uploader-chip-size">{tShared('shared.upload.uploading_percent', { pct: uploadPercent })}</span>
            </li>
          )}
        </ul>
      )}

      {error && (
        <span role="status" className="attachment-uploader-error">
          {error}
        </span>
      )}

      <button
        type="button"
        className="attachment-uploader-button"
        onClick={handleAttachClick}
        disabled={buttonDisabled}
        aria-label={atLimit ? tShared('shared.upload.at_limit_aria', { max: state.maxItems }) : tShared('shared.upload.attach_aria')}
        aria-busy={!!uploadingFile}
        title={atLimit ? tShared('shared.upload.at_limit_title', { max: state.maxItems }) : undefined}
      >
        <Paperclip size={16} />
        {label && <span className="attachment-uploader-label">{label}</span>}
        {attachments.length > 0 && (
          <span className="attachment-uploader-count" aria-hidden>
            {attachments.length}/{state.maxItems}
          </span>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        className="attachment-uploader-file-hidden"
        onChange={handleFileSelected}
        aria-hidden
      />
    </div>
  );
}
